import type { AnswersByQuestion, ContentItem } from "../layout/documentLayout";

// Pure logic for choice questions (radio / check / dropDown) with `parentAnswer`: the child is
// shown instead of its parent, an extra "exclusive" option is added, and every child change
// recomputes the parent's answer. A faithful port of mobile ScheduledVisitDocScreen
// (getMergedOptionsForChild, processExclusiveFieldLogic, isOnlyExclusiveFieldSelected,
// areAllChildrenExclusive, computeParentAnswerFromChild, initializeChildrenFromParentAnswers and
// the save plan in handleOnInsertAnswer) — keep the branch order identical to mobile.
// Both the child and the parent answers are saved at the row they are stored at, else chartRow -1
// (mobile always writes -1; a stored answer at another row would get a duplicate row).

type ParentAnswerMapping = NonNullable<ContentItem["parentAnswer"]>;
type ExclusiveField = NonNullable<ParentAnswerMapping["exclusiveField"]>;

export type ParentAnswerWrite = { answer: unknown } | "keep";
export type ChildAnswerWrite = { answer: unknown } | "skip";

function parentAnswerOf(item: ContentItem): ParentAnswerMapping | null {
  return item.parentAnswer ?? null;
}

function exclusiveFieldOf(item: ContentItem): ExclusiveField | null {
  return parentAnswerOf(item)?.exclusiveField ?? null;
}

function isEmptyValue(value: unknown): boolean {
  // mobile: `!answer || (Array.isArray(answer) && answer.length === 0)`
  return !value || (Array.isArray(value) && value.length === 0);
}

/** The child's options plus the exclusive label, first or last (default) per its config. */
export function exclusiveOptions(child: ContentItem): string[] {
  const parentAnswer = parentAnswerOf(child);
  if (parentAnswer === null || child.possibleAnswers === undefined) {
    return child.possibleAnswers ?? [];
  }

  const options = [...child.possibleAnswers];
  const exclusiveField = parentAnswer.exclusiveField;
  if (exclusiveField !== undefined) {
    if (exclusiveField.position === "first") {
      options.unshift(exclusiveField.label);
    } else {
      options.push(exclusiveField.label);
    }
  }
  return options;
}

/**
 * For a check child: the exclusive option and the other options never stay ticked together.
 * `prev` is the value before the click, `next` the value with the click applied.
 */
export function applyCheckExclusivity(
  child: ContentItem,
  prev: string[],
  next: string[]
): string[] {
  const exclusiveLabel = exclusiveFieldOf(child)?.label;
  if (exclusiveLabel === undefined) {
    return next;
  }

  const hasExclusive = next.includes(exclusiveLabel);
  const hasOthers = next.some((value) => value !== exclusiveLabel);
  const hadExclusive = prev.includes(exclusiveLabel);

  if (hasExclusive && hasOthers) {
    // The exclusive option was just ticked -> keep only it; otherwise another option was ticked
    // -> drop the exclusive one.
    return hadExclusive ? next.filter((value) => value !== exclusiveLabel) : [exclusiveLabel];
  }
  if (hasExclusive && !hasOthers) {
    return [exclusiveLabel];
  }
  if (!hasExclusive && hasOthers) {
    return [...next];
  }
  return [];
}

/** Mobile's in-memory value for a child: `[label]` when the parent holds the exclusive value. */
function mobileChildState(
  child: ContentItem,
  childAnswer: unknown,
  parentAnswer: unknown
): unknown {
  const exclusiveField = exclusiveFieldOf(child);
  if (exclusiveField !== null && parentAnswer === exclusiveField.value) {
    return [exclusiveField.label];
  }
  return childAnswer;
}

/**
 * The value the child's editor starts from. The exclusive option is never stored on the child, so
 * it is shown when the parent holds `exclusiveField.value`. Mobile loads `[label]` for every type;
 * a radio / dropDown editor holds a single string, so it gets the label itself (same selection).
 */
export function loadChildValue(
  child: ContentItem,
  childAnswer: unknown,
  parentAnswer: unknown
): unknown {
  const value = mobileChildState(child, childAnswer, parentAnswer);
  if (value === childAnswer || child.itemType === "check") {
    return value;
  }
  return exclusiveFieldOf(child)?.label;
}

function isOnlyExclusiveFieldSelected(child: ContentItem, answer: unknown): boolean {
  const exclusiveLabel = exclusiveFieldOf(child)?.label;
  if (exclusiveLabel === undefined) {
    return false;
  }
  const answerArray: unknown[] = Array.isArray(answer) ? answer : [answer];
  return answerArray.length === 1 && answerArray[0] === exclusiveLabel;
}

interface ComputeContext {
  child: ContentItem;
  /** The parent's children that have `parentAnswer` (the child itself may be among them). */
  childrenWithParentAnswer: ContentItem[];
  currentParentAnswer: unknown;
  /** Mobile's state value of another child. */
  stateOf: (item: ContentItem) => unknown;
}

function areAllChildrenExclusive(context: ComputeContext): boolean {
  return context.childrenWithParentAnswer.every((sibling) => {
    if (sibling.id === context.child.id) {
      return true;
    }
    const answer = context.stateOf(sibling);
    if (isEmptyValue(answer)) {
      return true;
    }
    const siblingExclusiveLabel = exclusiveFieldOf(sibling)?.label;
    return (
      Array.isArray(answer) &&
      answer.length === 1 &&
      siblingExclusiveLabel !== undefined &&
      answer[0] === siblingExclusiveLabel
    );
  });
}

/** mobile computeParentAnswerFromChild */
function computeParentAnswerFromChild(context: ComputeContext, childAnswer: unknown): unknown {
  const { child } = context;
  const parentAnswer = parentAnswerOf(child);
  if (parentAnswer === null) {
    return null;
  }
  const exclusiveField = parentAnswer.exclusiveField;
  const mapping = parentAnswer.mapping;
  const otherOption = parentAnswer.otherOption;

  const answerArray: unknown[] = Array.isArray(childAnswer)
    ? childAnswer
    : childAnswer !== null && childAnswer !== undefined
    ? [childAnswer]
    : [];

  if (answerArray.length === 0) {
    const allChildrenEmpty = context.childrenWithParentAnswer.every((sibling) =>
      isEmptyValue(sibling.id === child.id ? childAnswer : context.stateOf(sibling))
    );
    return allChildrenEmpty ? null : context.currentParentAnswer;
  }

  if (isOnlyExclusiveFieldSelected(child, childAnswer)) {
    if (exclusiveField !== undefined && areAllChildrenExclusive(context)) {
      return exclusiveField.value;
    }
  }

  const selectedOptions = answerArray.filter(
    (option) => option !== exclusiveField?.label && option !== ""
  );
  if (selectedOptions.length > 0) {
    // "Other" is any selected value that is not one of the child's options.
    const hasOtherOption =
      child.showOtherOption === true &&
      selectedOptions.some(
        (option) => typeof option !== "string" || !(child.possibleAnswers ?? []).includes(option)
      );
    if (hasOtherOption && otherOption !== undefined) {
      return otherOption;
    }

    for (const option of selectedOptions) {
      const mappedValue = typeof option === "string" ? mapping[option] : undefined;
      if (typeof mappedValue === "string" && mappedValue !== "") {
        return mappedValue;
      }
    }

    // No mapped option: keep the current parent value.
    return context.currentParentAnswer;
  }

  return null;
}

function sameAnswer(a: unknown, b: unknown): boolean {
  return (a ?? null) === (b ?? null);
}

/**
 * What to write when a parentAnswer child changes (mobile handleOnInsertAnswer):
 * - child: saved as is, unless only the exclusive option is selected — then it is deleted (null)
 *   when it had a value, otherwise nothing is sent;
 * - parent: the recomputed answer; "keep" when it equals the parent's current answer.
 * `childValue` must already have gone through `applyCheckExclusivity` for a check child.
 * `previousChildValue` is the child's value before the change (its stored answer, or the loaded
 * value — a needless `null` for a row that does not exist is harmless).
 */
export function planParentAnswerSave(params: {
  answers: AnswersByQuestion;
  child: ContentItem;
  childValue: unknown;
  parent: ContentItem;
  previousChildValue: unknown;
  siblings: ContentItem[];
}): { childWrite: ChildAnswerWrite; parentWrite: ParentAnswerWrite } {
  const { answers, child, childValue, parent, previousChildValue, siblings } = params;

  if (parentAnswerOf(child) === null) {
    return { childWrite: { answer: childValue }, parentWrite: "keep" };
  }

  const storedAnswer = (item: ContentItem): unknown => answers.get(item.id)?.[0]?.answer;
  const currentParentAnswer = storedAnswer(parent);
  const computed = computeParentAnswerFromChild(
    {
      child,
      childrenWithParentAnswer: siblings.filter((sibling) => parentAnswerOf(sibling) !== null),
      currentParentAnswer,
      stateOf: (sibling) => mobileChildState(sibling, storedAnswer(sibling), currentParentAnswer),
    },
    childValue
  );

  const childWrite: ChildAnswerWrite = (() => {
    if (!isOnlyExclusiveFieldSelected(child, childValue)) {
      return { answer: childValue };
    }
    const hadValue =
      Boolean(previousChildValue) &&
      (Array.isArray(previousChildValue) ? previousChildValue.length > 0 : true);
    return hadValue ? { answer: null } : "skip";
  })();

  const parentWrite: ParentAnswerWrite = sameAnswer(computed, currentParentAnswer)
    ? "keep"
    : { answer: computed ?? null };

  return { childWrite, parentWrite };
}

// ---- Editor glue: where the parent is, what the child shows, what Save writes ----

/** The parent a parentAnswer child syncs and its children; null when `child` has no parentAnswer. */
export function parentAnswerFamily(
  child: ContentItem,
  byId: Map<number, ContentItem>
): { parent: ContentItem; siblings: ContentItem[] } | null {
  if (parentAnswerOf(child) === null || child.parentId === null) {
    return null;
  }
  const parent = byId.get(child.parentId);
  if (parent === undefined) {
    return null;
  }
  return { parent, siblings: parent.columns?.[0] ?? [] };
}

/**
 * The value a parentAnswer child shows (view and editor alike): `loadChildValue` over the stored
 * child and parent answers. Any other question shows its own stored answer.
 */
export function displayedChildValue(
  child: ContentItem,
  answers: AnswersByQuestion,
  byId: Map<number, ContentItem>
): unknown {
  const childAnswer: unknown = answers.get(child.id)?.[0]?.answer;
  const family = parentAnswerFamily(child, byId);
  if (family === null) {
    return childAnswer;
  }
  return loadChildValue(child, childAnswer, answers.get(family.parent.id)?.[0]?.answer);
}

/** The row a question's answer is stored at (-1 when it has none yet), like the editors' onSave. */
function storedChartRow(answers: AnswersByQuestion, questionId: number): number {
  return answers.get(questionId)?.[0]?.chartRow ?? -1;
}

/**
 * The `/answer` writes of a parentAnswer child's Save, in mobile's order: the child (unless
 * skipped), then the parent. Each at the row its answer is stored at, else chartRow -1 (mobile). `childValue` is the editor's value
 * (a check child already went through `applyCheckExclusivity`); the previous child value is the
 * stored answer.
 */
export function parentAnswerWrites(params: {
  answers: AnswersByQuestion;
  byId: Map<number, ContentItem>;
  child: ContentItem;
  childValue: unknown;
}): { answer: unknown; chartRow: number; questionId: number }[] {
  const { answers, byId, child, childValue } = params;
  const family = parentAnswerFamily(child, byId);
  if (family === null) {
    return [
      { questionId: child.id, chartRow: storedChartRow(answers, child.id), answer: childValue },
    ];
  }
  const plan = planParentAnswerSave({
    answers,
    child,
    childValue,
    parent: family.parent,
    previousChildValue: answers.get(child.id)?.[0]?.answer,
    siblings: family.siblings,
  });
  const writes: { answer: unknown; chartRow: number; questionId: number }[] = [];
  if (plan.childWrite !== "skip") {
    writes.push({
      questionId: child.id,
      chartRow: storedChartRow(answers, child.id),
      answer: plan.childWrite.answer,
    });
  }
  if (plan.parentWrite !== "keep") {
    writes.push({
      questionId: family.parent.id,
      chartRow: storedChartRow(answers, family.parent.id),
      answer: plan.parentWrite.answer,
    });
  }
  return writes;
}
